// src/components/gdpr/CookieBanner.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  FormGroup,
  Collapse,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useT } from '../../i18n/useT';
import {
  enableAnalytics,
  disableAnalytics,
} from '../../utils/analytics';
import { useAuthStore } from '../../stores/authStore';

interface CookieConsent {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieBannerProps {
  onAccept: (consent: CookieConsent) => void;
  onDecline: () => void;
}

const COOKIE_CONSENT_KEY = 'instant-tours-cookie-consent';

export const CookieBanner: React.FC<CookieBannerProps> = ({
  onAccept,
  onDecline,
}) => {
  const t = useT();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Always required
    functional: true,
    analytics: false,
    marketing: false,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Check if user has already provided consent
    const existingConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!existingConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Reach into the auth store imperatively so a banner accept
  // persists consent to Firestore as well as localStorage. The
  // store action handles the "no auth user yet" race by warning
  // and skipping; that's fine because anonymous sign-in usually
  // resolves before the banner appears (banner has the 1s
  // mount delay).
  const recordGdprConsent = useAuthStore((s) => s.recordGdprConsent);

  /**
   * Apply a consent decision: persist locally, propagate to the
   * analytics installer (so GA either loads or is suppressed),
   * and write to Firestore via the auth store. Used by all three
   * action paths (accept-all, accept-selected, decline-all) so
   * they share the side-effect set rather than each rolling its
   * own.
   */
  const applyConsent = (next: CookieConsent) => {
    try {
      localStorage.setItem(
        COOKIE_CONSENT_KEY,
        JSON.stringify({ ...next, timestamp: Date.now() })
      );
    } catch {
      /* private mode / quota — degrade silently */
    }

    // Toggle the GA loader. enableAnalytics is idempotent and
    // disableAnalytics also clears any in-progress install.
    if (next.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }

    // Best-effort: persist to Firestore so the choice survives
    // device switches and feeds the server-side gating that
    // gates marketing emails / personalized content. Don't await
    // — the banner dismisses immediately and a slow Firestore
    // write shouldn't hold up the UI.
    void recordGdprConsent({
      necessary: true,
      functional: next.functional,
      analytics: next.analytics,
      marketing: next.marketing,
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('CookieBanner: recordGdprConsent failed:', err);
    });
  };

  const handleAcceptAll = () => {
    const fullConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    applyConsent(fullConsent);
    onAccept(fullConsent);
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    applyConsent(consent);
    onAccept(consent);
    setIsVisible(false);
  };

  const handleDeclineAll = () => {
    const minimalConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    applyConsent(minimalConsent);
    onDecline();
    setIsVisible(false);
  };

  const handleConsentChange = (category: keyof CookieConsent) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConsent(prev => ({
      ...prev,
      [category]: event.target.checked,
    }));
  };

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 3,
          borderRadius: 2,
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        <Typography variant="h6" gutterBottom>
          
          {t("🍪 Cookie Settings")}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          
          {t("We use cookies to enhance your experience, provide personalized content, and analyze our traffic. You can customize your preferences below or")}{' '}
          <Link href="/privacy" target="_blank" rel="noopener">
            
            {t("learn more in our Privacy Policy")}
          </Link>.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Button
            onClick={() => setShowDetails(!showDetails)}
            endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            {showDetails ? 'Hide Details' : 'Customize Settings'}
          </Button>
        </Box>

        <Collapse in={showDetails}>
          <Box sx={{ mb: 3 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.necessary}
                    disabled // Always required
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      
                      {t("Necessary Cookies")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      
                      {t("Required for basic site functionality and security")}
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.functional}
                    onChange={handleConsentChange('functional')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      
                      {t("Functional Cookies")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      
                      {t("Remember your preferences and settings")}
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.analytics}
                    onChange={handleConsentChange('analytics')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      
                      {t("Analytics Cookies")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      
                      {t("Help us understand how you use our site")}
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.marketing}
                    onChange={handleConsentChange('marketing')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      
                      {t("Marketing Cookies")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      
                      {t("Currently not used - placeholder for future features")}
                    </Typography>
                  </Box>
                }
              />
            </FormGroup>
          </Box>
        </Collapse>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleDeclineAll}
            size="small"
          >
            
            {t("Essential Only")}
          </Button>
          
          {showDetails && (
            <Button
              variant="outlined"
              onClick={handleAcceptSelected}
              size="small"
            >
              
              {t("Save Preferences")}
            </Button>
          )}
          
          <Button
            variant="contained"
            onClick={handleAcceptAll}
            size="small"
          >
            
            {t("Accept All")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};